import { useState, useEffect, useMemo, useRef } from 'react';
import { Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { formatTimeTyping, isCompleteValidTime, maskTypedTime, parseTypedTime } from '@common-components/schedule-picker/time-input-utils';
import { formatClockTime, resolveUses12h } from '@common/config/time-format';
import { useClockPreference } from '@common/hooks/use-clock-preference';
import moment from 'moment';
import { isTimeInErrorState } from '@common-components/schedule-picker/time-input-validity';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { hoverWash } from './picker-colors';

interface BTTimePickerProps {
  value?: string; // Format: HH:mm
  onChange?: (value: string) => void; // Format: HH:mm
  onValidityChange?: (isValid: boolean) => void; // FE#312: report whether the typed value is a complete, in-range HH:mm
  primaryColor?: string; // Primary color for border and hover (default: #3b82f6 blue)
  lightColor?: string; // Light color for selected background (default: #e0f2fe light blue)
  darkColor?: string; // Dark color for selected text (default: #0369a1 dark blue)
  /** Ink ON the `darkColor` fill (the selected time). Defaults to white, which
   *  is right for a fixed dark literal — but a THEMED fill turns light sky blue
   *  in dark mode, where white on it is 2.1:1; a themed caller passes its pair. */
  onDarkColor?: string;
  zIndex?: number; // Z-index for the popover content
  disabled?: boolean; // Whether the time picker is disabled
  /** Custom popover trigger. When supplied, the bordered input pill is NOT
   *  rendered — this element becomes the sole trigger and the time list anchors
   *  to it (`PopoverTrigger asChild`, so it must forward ref + props; a native
   *  <button> does). Used by the compose dialog's summary line, where the time
   *  is an inline clickable word rather than a field. In this mode the popover
   *  opens with a type-a-time field above the list (exact minutes, "9:47"). */
  trigger?: React.ReactNode;
  /** BE#657 — when false, the picker does NOT emit `onChange(now)` on mount for an
   *  empty value. The scheduled-compose pickers pass false so a fresh draft keeps an
   *  empty dueDate (Send stays gated) instead of being silently seeded to "now". Other
   *  callers keep the default (true). See the matching note in BTDatePicker. */
  seedDefaultOnMount?: boolean;
}

const TIME_OPTIONS: string[] = [];
for (let hour = 0; hour < 24; hour++) {
  for (let minute = 0; minute < 60; minute += 5) {
    const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    TIME_OPTIONS.push(timeString);
  }
}

// Get default time (current time in HH:mm format)
const getDefaultTime = (): string => {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

export default function BTTimePicker({
  value = '',
  onChange,
  onValidityChange,
  primaryColor = '#3b82f6', // Default blue for border
  lightColor = '#e0f2fe', // Default light blue for selected background
  darkColor = '#0369a1', // Default dark blue for selected text
  onDarkColor = '#ffffff',
  zIndex = 20050, // Default high z-index to appear above dialogs
  disabled = false,
  trigger,
  seedDefaultOnMount = true
}: BTTimePickerProps) {
  const { t, i18n } = useTranslation(namespace);
  const [open, setOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const hasInitializedRef = useRef(false);
  // The list has 288 options. Opening at 00:00 (the top) made the current time
  // and everything after it unreachable — especially inside the schedule dialog,
  // whose body scroll-lock (react-remove-scroll) eats native wheel events on this
  // portaled popover. selectedRef + the open effect bring the current time into
  // view; the wheel handler scrolls the list manually so it works despite the lock.
  const selectedRef = useRef<HTMLButtonElement | null>(null);

  // Get default time only once on mount
  const defaultTime = useMemo(() => getDefaultTime(), []);
  const displayValue = value || defaultTime;
  const [inputValue, setInputValue] = useState(displayValue);

  // The option to scroll to on open: the 5-min mark NEAREST the current value.
  // The exact value is rarely a 5-min mark (e.g. 14:23), so matching the option
  // by equality would find nothing and the list would stay at 00:00.
  const nearestOptionTime = useMemo(() => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(inputValue || displayValue);
    if (!m) return null;
    const rounded = (Math.round((+m[1] * 60 + +m[2]) / 5) * 5) % (24 * 60);
    return `${String(Math.floor(rounded / 60)).padStart(2, '0')}:${String(rounded % 60).padStart(2, '0')}`;
  }, [inputValue, displayValue]);

  // Initialize with default if no value provided (only once on mount)
  useEffect(() => {
    if (!seedDefaultOnMount) return; // BE#657 — compose pickers opt out of now-seeding
    if (!hasInitializedRef.current && !value && onChange) {
      hasInitializedRef.current = true;
      onChange(defaultTime);
    }
  }, [value, onChange, defaultTime, seedDefaultOnMount]);

  // Sync with external value changes — FE#312: but NEVER while the user is typing,
  // or it snaps their in-progress value back to the committed/default time.
  useEffect(() => {
    if (!isFocused) {
      setInputValue(value || defaultTime);
    }
    if (value) {
      hasInitializedRef.current = true;
    }
  }, [value, defaultTime, isFocused]);

  // FE#312: report validity so the dialog can disable submit until the time is a
  // complete, in-range HH:mm (instead of auto-correcting a partial value).
  useEffect(() => {
    onValidityChange?.(isCompleteValidTime(inputValue));
  }, [inputValue, onValidityChange]);

  const handleTimeSelect = (time: string) => {
    setInputValue(time);
    onChange?.(time);
    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // FE#312: leave the partial/invalid value EXACTLY as typed — no mid-typing
    // auto-correct. formatTimeTyping only inserts the ":" as digits are entered
    // (it never clamps or completes). We commit ONLY a complete, in-range HH:mm;
    // anything else stays as typed and the error state + disabled submit signal it.
    const formatted = formatTimeTyping(e.target.value);
    setInputValue(formatted);

    if (isCompleteValidTime(formatted)) {
      onChange?.(formatted);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    // FE#312: CLEAR the field on focus so typing always builds a FRESH HH:mm.
    // The field is prefilled with a full time; relying on select-all to let the
    // first keystroke replace it is unreliable — a real mouse's mouseup fires
    // after focus and collapses the selection, so typing then APPENDS into the
    // prefilled value and mangles it (e.g. "11:03" + "1" at the caret -> "11:10",
    // never "12:30"). Starting from empty is deterministic: no selection race and
    // nothing to append into. The last committed value is restored on blur if the
    // user leaves without typing a complete time.
    setInputValue('');
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    // FE#312: never auto-correct. A complete valid time is already committed via
    // onChange; anything else (empty or partial) restores the last committed value
    // so the field is never left blank or half-typed.
    if (!isCompleteValidTime(inputValue)) {
      setInputValue(value || defaultTime);
    }
  };

  // On open, center the currently-selected time so it (and its neighbours) are
  // visible without scrolling. rAF waits for the portaled content to mount;
  // scrollIntoView finds the popover's own scroll container (the page is locked).
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      selectedRef.current?.scrollIntoView({ block: 'center' });
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Scroll the list in JS on wheel. The dialog's scroll-lock preventDefaults the
  // native wheel on this portaled popover, so moving scrollTop ourselves (on the
  // scroll container = the popover content = e.currentTarget) is what makes the
  // list scrollable at all here. This handler runs regardless of the lock.
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    // The lock preventDefaults the native wheel on this portaled popover, so it
    // does not scroll on its own; move scrollTop ourselves. (No preventDefault
    // here — React's onWheel is passive, so it would only log an error, and with
    // the native scroll already suppressed by the lock there is nothing to double.)
    e.currentTarget.scrollTop += e.deltaY;
  };

  const showError = isTimeInErrorState(inputValue);

  // ── Trigger mode: type an exact time ────────────────────────────────────
  // The list moves in 5-minute steps; people also want "9:47". The popover
  // opens with a field pre-filled with the current time and fully selected, so
  // typing simply replaces it. The list follows what is typed; Enter (or just
  // clicking away) commits it, Escape abandons it. A click on a list row still
  // works exactly as before.
  const typeInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const skipCommitRef = useRef(false);
  const [typed, setTyped] = useState('');
  // FE#535 — display only: the saved 12h/24h preference (flag-gated) decides the clock the list
  // is labelled in and the typed input is read as. The emitted HH:mm is the same either way.
  const clockPreference = useClockPreference();
  const uses12h = useMemo(
    () => resolveUses12h(i18n.language, clockPreference),
    [i18n.language, clockPreference],
  );
  const typedTime = useMemo(
    () => parseTypedTime(typed, { uses12h, referenceHHmm: value || displayValue }),
    [typed, uses12h, value, displayValue],
  );
  const typedInvalid = typed.trim() !== '' && !typedTime;
  // Trigger mode labels the rows in the locale's own clock (9:45 PM / 21:45),
  // matching the sentence the picker was opened from.
  const listLabel = (time: string) => formatClockTime(moment(time, 'HH:mm'), i18n.language, clockPreference);

  // Keep the list scrolled to the 5-minute row nearest what is being typed.
  // Keyed on a BOOLEAN, never the `trigger` element itself: the parent builds a
  // fresh element every render (its clock ticks), and depending on it re-ran
  // this effect and yanked a hand-scrolled list back to the current time.
  const hasTrigger = !!trigger;
  useEffect(() => {
    if (!hasTrigger || !open || !typedTime) return;
    const [h, m] = typedTime.split(':').map(Number);
    const rounded = (Math.round((h * 60 + m) / 5) * 5) % (24 * 60);
    const key = `${String(Math.floor(rounded / 60)).padStart(2, '0')}:${String(rounded % 60).padStart(2, '0')}`;
    listRef.current?.querySelector(`[data-time="${key}"]`)?.scrollIntoView({ block: 'center' });
  }, [hasTrigger, open, typedTime]);

  const handleOpenChange = (next: boolean) => {
    if (next && trigger) {
      skipCommitRef.current = false;
      // BE#657 — pre-fill ONLY a committed value. On a fresh compose (no dueDate yet, since the
      // compose pickers no longer self-seed "now") leave the field empty, so opening-and-closing the
      // picker can't manufacture a near-now time the user never chose (the seed-removal invariant).
      // Typing or picking still commits normally. When a value exists, Noam's pre-fill-and-keep
      // behaviour is unchanged.
      setTyped(value ? listLabel(value) : '');
    }
    // Clicking away keeps what was typed — as if Enter had been pressed. (typedTime is null when the
    // field is empty, so an untouched fresh-compose picker commits nothing.)
    if (!next && trigger && !skipCommitRef.current && typedTime && typedTime !== value) {
      setInputValue(typedTime);
      onChange?.(typedTime);
    }
    setOpen(next);
  };

  const handleTypedKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (typedTime) handleTimeSelect(typedTime);
  };

  return (
    <Popover open={disabled ? false : open} onOpenChange={disabled ? undefined : handleOpenChange}>
      {trigger ? (
        // Summary-line mode: the caller's inline word IS the trigger; no pill.
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      ) : (
      <PopoverAnchor asChild>
        <div 
          className={cn(
            "flex items-center justify-center w-full max-w-[160px] h-8 min-h-8 rounded-[20px] bg-transparent border transition-[background-color,border-color] duration-200",
            showError ? "border-red-500 dark:border-red-500" : "border-slate-200 dark:border-slate-700",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          // FE#312: force the red border on error so it wins over the imperative
          // hover border set below (which React does not manage).
          style={showError ? { borderColor: '#ef4444' } : undefined}
          onMouseEnter={disabled ? undefined : (e) => {
            e.currentTarget.style.backgroundColor = hoverWash(primaryColor);
            if (!showError) e.currentTarget.style.borderColor = primaryColor;
          }}
          onMouseLeave={disabled ? undefined : (e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            if (!showError) e.currentTarget.style.borderColor = '';
          }}
        >
          <Input
            value={inputValue}
            onChange={disabled ? undefined : handleInputChange}
            onFocus={disabled ? undefined : handleInputFocus}
            onBlur={disabled ? undefined : handleInputBlur}
            disabled={disabled}
            type="text"
            maxLength={5}
            aria-invalid={showError}
            placeholder={t('dateTime.timePlaceholder')}
            className={cn(
              "h-[30px] w-[55px] text-center text-sm font-medium border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent !bg-transparent px-0 pe-0",
              showError ? "text-red-600" : "text-[var(--color-text)]"
            )}
            style={{ backgroundColor: 'transparent', paddingInlineEnd: 0, color: showError ? '#dc2626' : undefined }}
            inputMode="numeric"
            pattern="[0-9:]*"
          />
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              className={cn(
                // NOTE: Tailwind's logical `-ms-*` compiles to a physical margin-left
                // in this build, so it is not an RTL fix. The 5px tuck is symmetric
                // enough either way; left physical and honest.
                "h-6 w-6 p-0 text-[var(--color-text)] transition-colors rounded-md -ml-[5px] inline-flex items-center justify-center bg-transparent border-0 outline-none",
                disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
              )}
            >
              <Clock className="h-[18px] w-[18px]" />
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      )}
      <PopoverContent
        onWheel={handleWheel}
        align="center"
        side="top"
        sideOffset={8}
        zIndex={zIndex}
        onEscapeKeyDown={() => { skipCommitRef.current = true; }}
        // BE#657 dark-mode fix — the popover MUST use the dark-aware surface token, not a hardcoded
        // bg-white (which rendered a white box with light-on-white text in dark mode). The list rows
        // already use --color-text; the surface + sticky header now match in both themes.
        className="w-[140px] max-h-[280px] p-0 overflow-y-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] shadow-[0px_16px_40px_rgba(15,23,42,0.18)]"
      >
        {trigger && (
          // Sticky type-a-time header: dark-aware surface so it never floats as a white box over the
          // dark list (the operator's 5.9.27 screenshot bug).
          <div className="sticky top-0 z-[1] bg-[var(--color-surface)] px-2 pt-2 pb-1">
            <input
              ref={typeInputRef}
              value={typed}
              onChange={(e) => setTyped(maskTypedTime(e.target.value))}
              onKeyDown={handleTypedKeyDown}
              // The popover focuses this field on open (first focusable); select
              // its text so the first keystroke replaces it.
              autoFocus
              // (next frame: on mount the value lands after the focus does)
              onFocus={(e) => { const el = e.currentTarget; requestAnimationFrame(() => el.select()); }}
              aria-label={t('dateTime.typeTime', 'Type a time')}
              aria-invalid={typedInvalid}
              placeholder={uses12h ? '9:47 PM' : '21:47'}
              inputMode="text"
              autoComplete="off"
              spellCheck={false}
              className={cn(
                // The field is auto-selected on open; give ::selection explicit dark-aware colors so
                // the highlighted text is never light-on-white (or dark-on-dark) in either theme.
                "h-9 w-full rounded-lg border bg-transparent px-2 text-center text-sm font-semibold text-[var(--color-text)] outline-none transition-[border-color,box-shadow] duration-150",
                "selection:bg-[var(--color-accent)] selection:text-white",
                typedInvalid
                  ? "border-red-400 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.15)]"
                  : "border-[var(--color-border)] focus:border-[var(--color-accent)] focus:shadow-[0_0_0_3px_rgba(81,179,226,0.15)]"
              )}
            />
            {/* FE#455: with no am/pm typed, a 12h locale keeps the half of the day already chosen
                (so "8" after a PM time commits as 8:00 PM). Show the resolved time as it is typed,
                so the user sees "= 8:00 PM" BEFORE committing instead of discovering it afterwards. */}
            {uses12h && typedTime && !/[ap]/i.test(typed) && (
              <div
                data-testid="typed-time-hint"
                aria-live="polite"
                className="pt-1 text-center text-xs text-[var(--color-text-muted,var(--color-text))] opacity-70"
              >
                {`= ${listLabel(typedTime)}`}
              </div>
            )}
          </div>
        )}
        <div ref={listRef} className={cn("flex flex-col gap-1", trigger ? "pt-1 pb-2" : "py-2")}>
          {TIME_OPTIONS.map((time, index) => (
            <button
              key={index}
              ref={time === nearestOptionTime ? selectedRef : undefined}
              data-time={time}
              onClick={() => handleTimeSelect(time)}
              className={cn(
                "flex items-center justify-center mx-2 rounded-lg border border-transparent px-3 py-1.5 text-sm font-medium transition-all duration-150",
                "text-[var(--color-text)] hover:border-[var(--color-border-strong)] hover:bg-[var(--color-surface-muted)]",
                "cursor-pointer"
              )}
              style={inputValue === time ? {
                backgroundColor: darkColor,
                color: onDarkColor,
                borderColor: 'transparent'
              } : {}}
              onMouseEnter={(e) => {
                if (inputValue !== time) {
                  e.currentTarget.style.borderColor = 'var(--color-border-strong)';
                  e.currentTarget.style.backgroundColor = 'var(--color-surface-muted)';
                }
              }}
              onMouseLeave={(e) => {
                if (inputValue !== time) {
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.backgroundColor = 'transparent';
                } else {
                  e.currentTarget.style.backgroundColor = darkColor;
                  e.currentTarget.style.color = onDarkColor;
                  e.currentTarget.style.borderColor = 'transparent';
                }
              }}
            >
              {trigger ? listLabel(time) : time}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
